import { useCallback, useEffect, useRef, useState } from 'react';

export interface QueryState<T> {
  data?: T;
  error?: unknown;
  isLoading: boolean;
  isFetching: boolean;
  isSuccess: boolean;
  isError: boolean;
  refetch: () => Promise<T | undefined>;
}

type QueryOptions = {
  skip?: boolean;
  deps?: unknown[];
};

const queryCache = new Map<string, unknown>();
const inflightQueries = new Map<string, Promise<unknown>>();
const invalidationEpoch = new Map<string, number>();

function getInvalidationToken(key: string) {
  let token = 0;

  invalidationEpoch.forEach((epoch, prefix) => {
    if (key.startsWith(prefix)) {
      token += epoch;
    }
  });

  return token;
}

export function useQuery<T>(
  key: string,
  fetcher: () => Promise<T>,
  options: QueryOptions = {}
): QueryState<T> {
  const { skip = false, deps = [] } = options;
  const depsKey = JSON.stringify(deps);
  const invalidationToken = getInvalidationToken(key);
  const cached = queryCache.get(key) as T | undefined;
  const [data, setData] = useState<T | undefined>(cached);
  const [error, setError] = useState<unknown>();
  const [isFetching, setIsFetching] = useState(!skip && cached === undefined);
  const mountedRef = useRef(true);
  const fetcherRef = useRef(fetcher);

  fetcherRef.current = fetcher;

  const applyCachedValue = useCallback((value: T | undefined) => {
    if (value !== undefined) {
      setData(value);
      setError(undefined);
    }
  }, []);

  const refetch = useCallback(async () => {
    if (skip) {
      return undefined;
    }

    const existing = inflightQueries.get(key) as
      | Promise<T | undefined>
      | undefined;

    if (existing) {
      setIsFetching(true);

      try {
        const result = await existing;
        const cachedValue = queryCache.get(key) as T | undefined;

        if (mountedRef.current) {
          applyCachedValue(cachedValue ?? result);
        }

        return cachedValue ?? result;
      } finally {
        if (mountedRef.current) {
          setIsFetching(false);
        }
      }
    }

    setIsFetching(true);
    setError(undefined);

    const request = (async () => {
      try {
        const result = await fetcherRef.current();
        queryCache.set(key, result);

        if (mountedRef.current) {
          applyCachedValue(result);
        }

        return result;
      } catch (err) {
        if (mountedRef.current) {
          setError(err);
        }

        return undefined;
      } finally {
        inflightQueries.delete(key);
        if (mountedRef.current) {
          setIsFetching(false);
        }
      }
    })();

    inflightQueries.set(key, request);
    return request;
  }, [applyCachedValue, key, skip]);

  useEffect(() => {
    mountedRef.current = true;

    if (skip) {
      setIsFetching(false);
      return () => {
        mountedRef.current = false;
      };
    }

    const cachedValue = queryCache.get(key) as T | undefined;

    if (cachedValue !== undefined) {
      applyCachedValue(cachedValue);
      setIsFetching(false);
      return () => {
        mountedRef.current = false;
      };
    }

    void refetch();

    return () => {
      mountedRef.current = false;
    };
  }, [applyCachedValue, key, skip, refetch, depsKey, invalidationToken]);

  return {
    data,
    error,
    isLoading: isFetching && data === undefined && !error,
    isFetching,
    isSuccess: !!data && !error,
    isError: !!error,
    refetch,
  };
}

export function useLazyQuery<T, A>(
  fetcher: (arg: A) => Promise<T>
): [(arg: A) => Promise<T | undefined>, QueryState<T>] {
  const [data, setData] = useState<T | undefined>();
  const [error, setError] = useState<unknown>();
  const [isFetching, setIsFetching] = useState(false);
  const fetcherRef = useRef(fetcher);

  fetcherRef.current = fetcher;

  const trigger = useCallback(async (arg: A) => {
    setIsFetching(true);
    setError(undefined);

    try {
      const result = await fetcherRef.current(arg);
      setData(result);
      return result;
    } catch (err) {
      setError(err);
      return undefined;
    } finally {
      setIsFetching(false);
    }
  }, []);

  return [
    trigger,
    {
      data,
      error,
      isLoading: isFetching && data === undefined && !error,
      isFetching,
      isSuccess: !!data && !error,
      isError: !!error,
      refetch: async () => data,
    },
  ];
}

export function invalidateQueryCache(keyPrefix?: string) {
  if (!keyPrefix) {
    queryCache.clear();
    invalidationEpoch.clear();
    return;
  }

  Array.from(queryCache.keys()).forEach((key) => {
    if (key.startsWith(keyPrefix)) {
      queryCache.delete(key);
    }
  });

  invalidationEpoch.set(keyPrefix, (invalidationEpoch.get(keyPrefix) ?? 0) + 1);
}
